import { Controller } from "@hotwired/stimulus"
import { fill } from "../../lib/format"

const KEY = "toolio.pomodoro"

// Twenty-five on, five off, fifteen after four. The count of finished sessions
// lives in localStorage on this device and nowhere else.
export default class extends Controller {
  static targets = ["phase", "clock", "hint", "toggle", "sessions", "minutes", "streak"]
  static values = { focus: Number, short: Number, long: Number, messages: Object }

  connect() {
    this.toggleTarget.dataset.label = this.toggleTarget.textContent
    this.stats = this.load()
    this.round = 1
    this.phase = "focus"
    this.remaining = this.focusValue * 60
    this.render()
  }

  disconnect() {
    this.stop()
  }

  toggle() {
    this.running ? this.pause() : this.start()
  }

  start() {
    this.running = true
    this.endsAt = Date.now() + this.remaining * 1000
    this.tick()
    this.timer = setInterval(() => this.tick(), 250)
    this.toggleTarget.textContent = this.messagesValue.pause
  }

  pause() {
    this.stop()
    this.toggleTarget.textContent = this.messagesValue.resume
  }

  stop() {
    clearInterval(this.timer)
    this.running = false
  }

  skip() {
    this.advance(false)
  }

  reset() {
    this.stop()
    this.round = 1
    this.phase = "focus"
    this.remaining = this.focusValue * 60
    this.toggleTarget.textContent = this.startLabel
    this.render()
  }

  get startLabel() {
    return this.toggleTarget.dataset.label || this.toggleTarget.textContent
  }

  tick() {
    this.remaining = Math.max(0, Math.round((this.endsAt - Date.now()) / 1000))
    this.render()
    if (this.remaining === 0) {
      this.chime()
      this.advance(true)
    }
  }

  // Move to the next phase. A finished focus block counts towards the stats;
  // a skipped one does not.
  advance(completed) {
    this.stop()
    if (this.phase === "focus") {
      if (completed) this.record()
      this.phase = this.round % 4 === 0 ? "long" : "short"
    } else {
      this.round = this.phase === "long" ? 1 : this.round + 1
      this.phase = "focus"
    }
    this.remaining = this.length(this.phase) * 60
    this.toggleTarget.textContent = this.startLabel
    this.render()
  }

  length(phase) {
    return { focus: this.focusValue, short: this.shortValue, long: this.longValue }[phase]
  }

  render() {
    const m = Math.floor(this.remaining / 60), s = this.remaining % 60
    this.clockTarget.textContent = `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    const msg = this.messagesValue
    this.phaseTarget.textContent = { focus: msg.focus, short: msg.short_break, long: msg.long_break }[this.phase]
    this.hintTarget.textContent = this.phase === "focus"
      ? fill(msg.focus_hint, { round: this.round, next: this.round === 4 ? msg.long : msg.short })
      : (this.phase === "long" ? msg.long_hint : fill(msg.short_hint, { round: this.round + 1 }))
    document.title = this.running ? `${this.clockTarget.textContent} · ${this.phaseTarget.textContent}` : document.title
    this.sessionsTarget.textContent = this.stats.sessions
    this.minutesTarget.textContent = this.stats.minutes
    this.streakTarget.textContent = this.stats.streak
  }

  record() {
    this.stats.sessions += 1
    this.stats.minutes += this.focusValue
    this.save()
  }

  // Stats roll over per day: yesterday's sessions extend the streak, an
  // older date resets it.
  load() {
    const today = dateKey(new Date())
    let saved = {}
    try { saved = JSON.parse(localStorage.getItem(KEY)) || {} } catch { saved = {} }
    if (saved.date === today) return saved
    const yesterday = dateKey(new Date(Date.now() - 86400000))
    const streak = saved.date === yesterday && saved.sessions > 0 ? saved.streak : 0
    return { date: today, sessions: 0, minutes: 0, streak }
  }

  save() {
    if (this.stats.sessions === 1) this.stats.streak += 1
    try { localStorage.setItem(KEY, JSON.stringify(this.stats)) } catch { /* private mode: keep going without */ }
  }

  chime() {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)()
      const osc = ctx.createOscillator(), gain = ctx.createGain()
      osc.connect(gain); gain.connect(ctx.destination)
      osc.frequency.value = 880
      gain.gain.setValueAtTime(0.2, ctx.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6)
      osc.start(); osc.stop(ctx.currentTime + 0.6)
    } catch { /* no audio: the phase label still changes */ }
  }
}

function dateKey(date) {
  return date.toISOString().slice(0, 10)
}
